import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  isWeekend,
} from 'date-fns'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  DayCalculation,
  OTAChannel,
  LOSCategory,
  PAXCategory,
  DayPriceOverride,
  BusinessModel,
} from '../types'
import ReactDOM from 'react-dom'
import { CSSProperties } from 'react'

interface DateListProps {
  calculations: DayCalculation[]
  selectedDate?: Date
  otaChannels: OTAChannel[]
  losCategories: LOSCategory[]
  paxCategories: PAXCategory[]
  priceOverrides: DayPriceOverride[]
  startMonth: Date
  monthsToShow: number
  onDateSelect: (date: Date) => void
  basePrice: number
  onPriceOverride: (
    date: string,
    price: number,
    losId?: string,
    paxId?: string,
  ) => void
  selectedChannel?: string
  onChannelSelect?: (channelId: string) => void
  businessModel: BusinessModel
  scrollToDate?: Date
}

const DateList: React.FC<DateListProps> = ({
  calculations,
  selectedDate,
  otaChannels,
  losCategories,
  paxCategories,
  priceOverrides,
  startMonth = startOfMonth(new Date()),
  monthsToShow = 12,
  onDateSelect,
  basePrice,
  onPriceOverride,
  selectedChannel,
  onChannelSelect,
  businessModel,
  scrollToDate = new Date(),
}) => {
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(new Set())
  const [editingPrice, setEditingPrice] = useState<{
    date: string
    price: number
  } | null>(null)
  const [hoveredPrice, setHoveredPrice] = useState<{
    date: string
    channelId: string
  } | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{
    top: number
    left: number
  } | null>(null)
  const dateRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const cellRefs = useRef<{ [key: string]: HTMLSpanElement | null }>({})
  const hoverTimeoutRef = useRef<NodeJS.Timeout>()

  // Scroll to date when scrollToDate changes
  useEffect(() => {
    if (scrollToDate) {
      const dateStr = format(scrollToDate, 'yyyy-MM-dd')
      const element = dateRefs.current[dateStr]
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [scrollToDate])

  // Effect to measure tooltip height and width after it renders and update position
  useLayoutEffect(() => {
    const tooltipEl = tooltipRef.current
    if (hoveredPrice && tooltipEl) {
      const key = `${hoveredPrice.date}-${hoveredPrice.channelId}`
      const cellEl = cellRefs.current[key]
      if (cellEl) {
        const tooltipRect = tooltipEl.getBoundingClientRect()
        const cellRect = cellEl.getBoundingClientRect()
        const margin = 8
        const top = cellRect.top - tooltipRect.height - margin
        const left = cellRect.left + cellRect.width / 2 - tooltipRect.width / 2
        setTooltipPos({ top, left })
      }
    }
  }, [hoveredPrice])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const handleCellHover = (date: string, channelId: string) => {
    // Don't show tooltips for agency and owner columns
    if (channelId === 'agency' || channelId === 'owner') {
      return
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setHoveredPrice({ date, channelId })
  }

  const handleCellLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPrice(null)
      setTooltipPos(null)
    }, 100) // Small delay to prevent flashing when moving between cells
  }

  const enabledChannels = otaChannels.filter((channel) => channel.enabled)

  const getCalculationForDate = (date: Date): DayCalculation | undefined => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return calculations.find((calc) => calc.date === dateStr)
  }

  const getPriceRange = (calculation: DayCalculation) => {
    const priceData: Array<{
      price: number
      isValid: boolean
      exceedsMax: boolean
      belowMin: boolean
    }> = []

    // Collect prices from all channels and LOS/PAX combinations
    Object.keys(calculation.prices).forEach((channelId) => {
      Object.keys(calculation.prices[channelId]).forEach((losId) => {
        Object.keys(calculation.prices[channelId][losId]).forEach((paxId) => {
          const price = calculation.prices[channelId][losId][paxId]
          priceData.push({
            price: price.finalPrice,
            isValid: price.isValid,
            exceedsMax: price.exceedsMax,
            belowMin: price.belowMin,
          })
        })
      })
    })

    if (priceData.length === 0) return null

    const prices = priceData.map((p) => p.price)
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const avgPrice =
      prices.reduce((sum, price) => sum + price, 0) / prices.length

    // Determine status for the range
    const hasErrors = priceData.some((p) => !p.isValid)
    const hasWarnings = priceData.some((p) => p.exceedsMax || p.belowMin)

    return {
      min: minPrice,
      avg: avgPrice,
      max: maxPrice,
      status: hasErrors ? 'error' : hasWarnings ? 'warning' : 'success',
      hasRange: minPrice !== maxPrice, // Show if there's actual price variation
    }
  }

  const isDateOverridden = (date: Date): boolean => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return priceOverrides.some((override) => override.date === dateStr)
  }

  const toggleMonth = (monthKey: string) => {
    const newCollapsed = new Set(collapsedMonths)
    if (newCollapsed.has(monthKey)) {
      newCollapsed.delete(monthKey)
    } else {
      newCollapsed.add(monthKey)
    }
    setCollapsedMonths(newCollapsed)
  }

  const generateMonthsWithDays = () => {
    const monthsWithDays: Array<{
      month: Date
      monthKey: string
      days: Date[]
      isCollapsed: boolean
    }> = []

    for (let i = 0; i < monthsToShow; i++) {
      const monthDate = addMonths(startMonth, i)
      const monthStart = startOfMonth(monthDate)
      const monthEnd = endOfMonth(monthDate)
      const days = eachDayOfInterval({ start: monthStart, end: monthEnd })
      const monthKey = format(monthDate, 'yyyy-MM')

      monthsWithDays.push({
        month: monthDate,
        monthKey,
        days,
        isCollapsed: collapsedMonths.has(monthKey),
      })
    }
    return monthsWithDays
  }

  const monthsWithDays = useMemo(() => generateMonthsWithDays(), [
    startMonth,
    monthsToShow,
    collapsedMonths,
  ])

  const defaultLos = losCategories.find((los) => los.isDefault)
  const defaultPax = paxCategories.find((pax) => pax.isDefault)

  const getDefaultPriceObject = (
    calculation: DayCalculation | undefined,
    channelId: string,
  ) => {
    if (!calculation || !defaultLos || !defaultPax) return null
    return (
      calculation.prices[channelId]?.[defaultLos.id]?.[defaultPax.id] ?? null
    )
  }

  // Agency commission helpers
  const getAgencyAbsoluteAmount = () => {
    return businessModel.agencyFee ?? 0
  }
  const getOwnerAmountAbsolute = (price: number | null) => {
    if (!price) return null
    return price - getAgencyAbsoluteAmount()
  }
  const getOwnerPayoutPercent = () => {
    return businessModel.ownerPayoutPercent ?? 0
  }
  const getOwnerAmountRelative = (price: number | null) => {
    if (!price) return null
    return price * (getOwnerPayoutPercent() / 100)
  }
  const getAgencyCommissionRelative = (price: number | null) => {
    if (!price) return null
    const ownerAmount = getOwnerAmountRelative(price)
    return ownerAmount !== null ? price - ownerAmount : null
  }

  // Helper to get the override price for a date
  const getOverridePriceForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    const override = priceOverrides.find((o) => o.date === dateStr)
    return override ? override.basePrice : null
  }

  // Helper to get the starting price for calculations (override or base)
  const getStartingPriceForDate = (date: Date) => {
    const override = getOverridePriceForDate(date)
    return override !== null && override !== undefined ? override : basePrice
  }

  // Update renderPriceTooltip to show both Base Price and Override Price (if set)
  const renderPriceTooltip = (
    calculation: DayCalculation,
    channelId: string,
  ) => {
    let channelToUse = channelId
    if (
      channelId === 'base' ||
      channelId === 'owner' ||
      channelId === 'agency'
    ) {
      channelToUse = enabledChannels[0]?.id || ''
    }
    const price = getDefaultPriceObject(calculation, channelToUse)
    if (!price) return null

    // Get the date for this calculation
    const dateStr = calculation.date
    const overridePrice = getOverridePriceForDate(new Date(dateStr))
    const startingPrice =
      overridePrice !== null && overridePrice !== undefined
        ? overridePrice
        : basePrice

    const tooltipStyle: CSSProperties = {
      position: 'fixed',
      top: tooltipPos ? tooltipPos.top : -9999,
      left: tooltipPos ? tooltipPos.left : -9999,
      zIndex: 9999,
    }

    let rows: {
      label: string
      percent?: string
      amount?: string
      subtotal: string
    }[] = []

    // Show both Base Price and Override Price (if set and different)
    rows.push({ label: 'Base Price', subtotal: `€${basePrice.toFixed(2)}` })
    if (
      overridePrice !== null &&
      overridePrice !== undefined &&
      overridePrice !== basePrice
    ) {
      const percentageDiff = ((overridePrice - basePrice) / basePrice) * 100
      const overridePercentage = `${
        percentageDiff > 0 ? '+' : ''
      }${percentageDiff.toFixed(1)}%`
      const deltaAmount = overridePrice - basePrice
      rows.push({
        label: 'Override Price',
        subtotal: `€${overridePrice.toFixed(2)}`,
        percent: overridePercentage,
        amount: `${deltaAmount >= 0 ? '+' : ''}€${deltaAmount.toFixed(2)}`,
      })
    }

    // Show LOS and PAX models used
    if (defaultLos) {
      rows.push({
        label: `LOS: ${defaultLos.name}`,
        percent: `${defaultLos.priceModifier}%`,
        subtotal: '',
      })
    }
    if (defaultPax) {
      rows.push({
        label: `PAX: ${defaultPax.name}`,
        percent: `${defaultPax.sellingPriceDifference ?? 0}${
          defaultPax.sellingPriceDifferenceType === 'percent' ? '%' : '€'
        }`,
        subtotal: '',
      })
    }

    // Build the breakdown as in the matrix, starting from the correct price
    let current = startingPrice
    if (price.ruleBreakdown && Array.isArray(price.ruleBreakdown)) {
      price.ruleBreakdown.forEach((rule) => {
        // Skip LOS and PAX rules since we already show them above
        if (rule.name.startsWith('LOS:') || rule.name.startsWith('PAX:')) {
          return
        }
        const diff = current * (rule.percent / 100)
        current += diff
        rows.push({
          label: rule.name,
          percent: `${rule.percent > 0 ? '+' : ''}${rule.percent}%`,
          amount: `${diff >= 0 ? '+' : ''}€${diff.toFixed(2)}`,
          subtotal: `€${current.toFixed(2)}`,
        })
      })
    }
    // Final price
    rows.push({
      label: 'Final Price',
      subtotal: `€${price.finalPrice.toFixed(2)}`,
    })
    // Owner/Agency rows (after final price)
    let ownerAgencyRows: typeof rows = []
    const priceForAgencyOwner = price.preChannelFeePrice || price.finalPrice
    if (businessModel.type === 'agency') {
      if (
        businessModel.model === 'absolute_same' ||
        businessModel.model === 'absolute_different'
      ) {
        const agencyAmount = businessModel.agencyFee || 0
        const ownerAmount = priceForAgencyOwner - agencyAmount
        ownerAgencyRows.push({
          label: 'Owner Amount',
          percent: '',
          amount: '',
          subtotal: `€${ownerAmount.toFixed(2)}`,
        })
        ownerAgencyRows.push({
          label: 'Agency Amount',
          percent: '',
          amount: '',
          subtotal: `€${agencyAmount.toFixed(2)}`,
        })
      } else if (businessModel.model === 'relative_percentage') {
        const ownerPercent = businessModel.ownerPayoutPercent || 0
        const ownerAmount = priceForAgencyOwner * (ownerPercent / 100)
        const agencyAmount = priceForAgencyOwner - ownerAmount
        ownerAgencyRows.push({
          label: 'Owner Amount',
          percent: `${ownerPercent}%`,
          amount: '',
          subtotal: `€${ownerAmount.toFixed(2)}`,
        })
        ownerAgencyRows.push({
          label: 'Agency Amount',
          percent: '',
          amount: '',
          subtotal: `€${agencyAmount.toFixed(2)}`,
        })
      }
    } else {
      // Direct model: only show Owner Amount
      ownerAgencyRows.push({
        label: 'Owner Amount',
        percent: '',
        amount: '',
        subtotal: `€${priceForAgencyOwner.toFixed(2)}`,
      })
    }
    // Channel Fee (always show if there is a difference)
    if (price.preChannelFeePrice !== price.finalPrice) {
      const channelFee = price.finalPrice - price.preChannelFeePrice
      ownerAgencyRows.push({
        label: 'Channel Fee',
        percent: '',
        amount: '',
        subtotal: `€${channelFee.toFixed(2)}`,
      })
    }

    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        style={tooltipStyle}
        className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs whitespace-pre font-mono min-w-[380px] max-w-[480px]"
      >
        <div className="flex flex-row justify-between items-baseline font-bold mb-1">
          <span className="text-left w-2/6 truncate" title="Step">
            Step
          </span>
          <span className="text-center w-1/6 px-2">%</span>
          <span className="text-right w-1/6 px-2">Δ</span>
          <span className="text-right w-2/6">Subtotal</span>
        </div>
        {rows.map((row, idx) => (
          <React.Fragment key={idx}>
            {row.label === 'Final Price' && (
              <div className="border-t border-gray-300 my-1"></div>
            )}
            <div className="flex flex-row justify-between items-baseline">
              <span className="text-left w-2/6 truncate" title={row.label}>
                {row.label}
              </span>
              <span className="text-center w-1/6 px-2">
                {row.percent || ''}
              </span>
              <span className="text-right w-1/6 px-2">{row.amount || ''}</span>
              <span className="text-right w-2/6">{row.subtotal}</span>
            </div>
          </React.Fragment>
        ))}
        {ownerAgencyRows.length > 0 && (
          <>
            <div className="border-t border-gray-300 my-1"></div>
            {ownerAgencyRows.map((row, idx) => (
              <div
                key={idx}
                className="flex flex-row justify-between items-baseline"
              >
                <span className="text-left w-2/6 truncate" title={row.label}>
                  {row.label}
                </span>
                <span className="text-center w-1/6">{row.percent || ''}</span>
                <span className="text-right w-1/6">{row.amount || ''}</span>
                <span className="text-right w-2/6">{row.subtotal}</span>
              </div>
            ))}
          </>
        )}
      </div>,
      document.body,
    )
  }

  // Update the price cell rendering to use the new hover handlers
  const renderPriceCell = (
    date: string,
    channelId: string,
    price: number | null,
  ) => {
    const key = `${date}-${channelId}`
    return (
      <span
        ref={(el) => (cellRefs.current[key] = el)}
        onMouseEnter={() => handleCellHover(date, channelId)}
        onMouseLeave={handleCellLeave}
        className="block w-full text-center text-sm font-medium text-gray-900"
      >
        {price !== null ? `€${price.toFixed(2)}` : '—'}
      </span>
    )
  }

  return (
    <div className="h-full bg-white border-r border-gray-200 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-2 z-10">
        <div className="flex items-center px-4 text-xs text-gray-600">
          <div className="w-40 flex-shrink-0"></div>
          <div className="flex-1"></div>
          <div className="w-24 px-2 flex items-center justify-center">
            <div className="text-center text-gray-500 mb-1">Base Price</div>
          </div>
          <div className="w-28 px-2 flex items-center justify-center">
            <div className="text-center text-gray-500 mb-1">Override Price</div>
          </div>
          <div className="flex pr-8">
            {enabledChannels.map((channel) => (
              <div
                key={channel.id}
                className="w-20 flex items-center justify-center"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="text-sm font-medium text-gray-900">
                    {channel.name}
                  </div>
                  <div className="text-[10px] text-gray-400">Final</div>
                </div>
              </div>
            ))}
            {/* Absolute models: agency fee and owner amount */}
            {(businessModel.model === 'absolute_same' ||
              businessModel.model === 'absolute_different') &&
              businessModel.type !== 'direct' && (
                <>
                  <div className="w-20 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-sm font-medium text-gray-900">
                        Agency
                      </div>
                      <div className="text-[10px] text-gray-400">Fee</div>
                    </div>
                  </div>
                  <div className="w-20 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-sm font-medium text-gray-900">
                        Owner
                      </div>
                      <div className="text-[10px] text-gray-400">Amount</div>
                    </div>
                  </div>
                </>
              )}
            {/* Relative model: agency commission and owner payout */}
            {businessModel.model === 'relative_percentage' &&
              businessModel.type !== 'direct' && (
                <>
                  <div className="w-20 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-sm font-medium text-gray-900">
                        Agency
                      </div>
                      <div className="text-[10px] text-gray-400">
                        Commission
                      </div>
                    </div>
                  </div>
                  <div className="w-20 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-sm font-medium text-gray-900">
                        Owner
                      </div>
                      <div className="text-[10px] text-gray-400">Payout</div>
                    </div>
                  </div>
                </>
              )}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-scroll">
        {monthsWithDays.map(({ month, monthKey, days, isCollapsed }) => (
          <div key={monthKey} className="relative">
            {/* Sticky month header, placed in an overlaying div */}
            <div className="sticky top-0 w-36 h-0 z-10">
              <div className="bg-white border-t-2 border-gray-200">
                <div className="flex items-center h-[60px] px-3">
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="flex items-center gap-2 w-full text-left hover:bg-gray-50 p-1 rounded transition-colors"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-3 h-3 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0" />
                    )}
                    <div className="text-sm font-medium text-gray-900">
                      {format(month, 'yyyy / MMM')}
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Days list with left padding to make space for the sticky header */}
            <div className="pl-40 border-t-2 border-gray-200">
              {isCollapsed ? (
                <div className="flex items-center p-3 border-b border-gray-100 h-[58px]">
                  <span className="text-sm text-gray-500">
                    Click to expand {days.length} days
                  </span>
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {days.map((date) => {
                    const calculation = getCalculationForDate(date)
                    const priceRange = calculation
                      ? getPriceRange(calculation)
                      : null
                    const hasOverride = isDateOverridden(date)
                    const isSelected =
                      selectedDate &&
                      format(date, 'yyyy-MM-dd') ===
                        format(selectedDate, 'yyyy-MM-dd')

                    return (
                      <div
                        key={format(date, 'yyyy-MM-dd')}
                        ref={(el) =>
                          (dateRefs.current[format(date, 'yyyy-MM-dd')] = el)
                        }
                        className={`relative flex items-center px-4 py-3 rounded-lg border cursor-pointer transition-colors hover:shadow-sm ${
                          isSelected
                            ? 'border-primary-300 bg-primary-50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        } ${
                          isWeekend(date) ? 'border-l-4 border-l-blue-500' : ''
                        } ${hasOverride ? 'price-overridden' : ''}`}
                        onClick={() => onDateSelect(date)}
                        title={`Click to view details for ${format(
                          date,
                          'EEEE, MMMM do, yyyy',
                        )}`}
                      >
                        {/* Date Info */}
                        <div className="w-40 flex-shrink-0 flex items-center gap-3">
                          <div className="font-medium text-gray-900 text-sm flex items-center gap-1">
                            <span className="w-10 text-right">
                              {format(date, 'd.MM')}
                            </span>
                            <span className="text-gray-400">·</span>
                            <span>{format(date, 'EEEE')}</span>
                          </div>
                          {hasOverride && (
                            <div
                              className="override-indicator"
                              title="Price overridden"
                            ></div>
                          )}
                        </div>
                        <div className="flex-1"></div>

                        {/* Base Price (read-only) */}
                        <div className="w-24 px-2 flex items-center justify-center">
                          <span className="text-sm text-gray-900">
                            €{basePrice.toFixed(2)}
                          </span>
                        </div>
                        {/* Override Price (editable) */}
                        <div className="w-28 px-2 flex items-center justify-center">
                          <div className="relative w-full">
                            <input
                              type="number"
                              className="w-full text-sm font-medium text-gray-900 border-gray-300 rounded-md pl-8 pr-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-400 text-right transition-all duration-150 hover:border-gray-400 focus:border-primary-400"
                              placeholder={
                                (editingPrice?.date ===
                                  format(date, 'yyyy-MM-dd') &&
                                  String(editingPrice.price) === '') ||
                                (!hasOverride && !getOverridePriceForDate(date))
                                  ? '—'
                                  : `€${getStartingPriceForDate(date)}`
                              }
                              value={
                                editingPrice?.date ===
                                format(date, 'yyyy-MM-dd')
                                  ? editingPrice.price
                                  : hasOverride
                                  ? priceOverrides.find(
                                      (o) =>
                                        o.date === format(date, 'yyyy-MM-dd'),
                                    )?.basePrice ?? ''
                                  : ''
                              }
                              onClick={(e) => {
                                e.stopPropagation()
                                const currentPrice = hasOverride
                                  ? priceOverrides.find(
                                      (o) =>
                                        o.date === format(date, 'yyyy-MM-dd'),
                                    )?.basePrice ??
                                    getStartingPriceForDate(date)
                                  : getStartingPriceForDate(date)
                                setEditingPrice({
                                  date: format(date, 'yyyy-MM-dd'),
                                  price: currentPrice,
                                })
                                const input = e.target as HTMLInputElement
                                input.select()
                              }}
                              onBlur={() => {
                                setEditingPrice(null)
                              }}
                              onChange={(e) => {
                                const raw = e.target.value
                                const num = parseFloat(raw)
                                if (!isNaN(num)) {
                                  setEditingPrice({
                                    date: format(date, 'yyyy-MM-dd'),
                                    price: num,
                                  })
                                  onPriceOverride(
                                    format(date, 'yyyy-MM-dd'),
                                    num,
                                  )
                                } else if (raw === '') {
                                  setEditingPrice(null)
                                  onPriceOverride(
                                    format(date, 'yyyy-MM-dd'),
                                    -1,
                                  )
                                }
                              }}
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                              €
                            </span>
                          </div>
                        </div>

                        {/* Price Range Display */}
                        <div className="flex pr-4">
                          {enabledChannels.map((channel) => {
                            const price = getDefaultPriceObject(
                              calculation,
                              channel.id,
                            )
                            return (
                              <div
                                key={channel.id}
                                className="w-20 flex items-center justify-center"
                              >
                                {renderPriceCell(
                                  format(date, 'yyyy-MM-dd'),
                                  channel.id,
                                  price?.finalPrice ?? null,
                                )}
                              </div>
                            )
                          })}
                          {/* Absolute models: agency fee and owner amount */}
                          {(businessModel.model === 'absolute_same' ||
                            businessModel.model === 'absolute_different') &&
                            businessModel.type !== 'direct' &&
                            (() => {
                              if (enabledChannels.length === 0) {
                                return (
                                  <>
                                    <div className="w-20 flex items-center justify-center">
                                      <span className="flex flex-col items-center justify-center w-full text-sm font-medium text-gray-900">
                                        —
                                      </span>
                                    </div>
                                    <div className="w-20 flex items-center justify-center">
                                      <span className="flex flex-col items-center justify-center w-full text-sm font-medium text-gray-900">
                                        —
                                      </span>
                                    </div>
                                  </>
                                )
                              }
                              const channel = enabledChannels[0]
                              const price = getDefaultPriceObject(
                                calculation,
                                channel?.id,
                              )
                              const agencyAmount = getAgencyAbsoluteAmount()
                              const ownerAmount = getOwnerAmountAbsolute(
                                price?.finalPrice ?? null,
                              )
                              return (
                                <>
                                  <div className="w-20 flex items-center justify-center">
                                    {renderPriceCell(
                                      format(date, 'yyyy-MM-dd'),
                                      'agency',
                                      agencyAmount,
                                    )}
                                  </div>
                                  <div className="w-20 flex items-center justify-center">
                                    {renderPriceCell(
                                      format(date, 'yyyy-MM-dd'),
                                      'owner',
                                      ownerAmount,
                                    )}
                                  </div>
                                </>
                              )
                            })()}
                          {/* Relative model: agency commission and owner payout */}
                          {businessModel.model === 'relative_percentage' &&
                            businessModel.type !== 'direct' &&
                            (() => {
                              if (enabledChannels.length === 0) {
                                return (
                                  <>
                                    <div className="w-20 flex items-center justify-center">
                                      <span className="flex flex-col items-center justify-center w-full text-sm font-medium text-gray-900">
                                        —
                                      </span>
                                    </div>
                                    <div className="w-20 flex items-center justify-center">
                                      <span className="flex flex-col items-center justify-center w-full text-sm font-medium text-gray-900">
                                        —
                                      </span>
                                    </div>
                                  </>
                                )
                              }
                              const channel = enabledChannels[0]
                              const price = getDefaultPriceObject(
                                calculation,
                                channel?.id,
                              )
                              const ownerAmount = getOwnerAmountRelative(
                                price?.finalPrice ?? null,
                              )
                              const commission = getAgencyCommissionRelative(
                                price?.finalPrice ?? null,
                              )
                              return (
                                <>
                                  <div className="w-20 flex items-center justify-center">
                                    {renderPriceCell(
                                      format(date, 'yyyy-MM-dd'),
                                      'agency',
                                      commission,
                                    )}
                                  </div>
                                  <div className="w-20 flex items-center justify-center">
                                    {renderPriceCell(
                                      format(date, 'yyyy-MM-dd'),
                                      'owner',
                                      ownerAmount,
                                    )}
                                  </div>
                                </>
                              )
                            })()}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
        {hoveredPrice && (
          <>
            {(() => {
              const calculation = getCalculationForDate(
                new Date(hoveredPrice.date),
              )
              if (!calculation) return null
              return renderPriceTooltip(calculation, hoveredPrice.channelId)
            })()}
          </>
        )}
      </div>
    </div>
  )
}

export default DateList
